// src/components/store/sections/navbar/NavbarV18CybercommandRoyalViolet.tsx
"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  Menu,
  Search,
  MapPin,
  ShoppingCart,
  ChevronDown,
  X,
  Package,
  Sparkles,
  Layers,
  Flame,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Navigation,
  Store,
  Camera,
  Phone,
  Heart,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import UserHeaderMenu from "@/components/store/UserHeaderMenu";
import CartHeaderButton from "@/components/store/CartHeaderButton";
import ShopCardsModal, { ShopItem } from "@/components/store/sections/shop/ShopCardsModal";
import VisualSearchModal from "./VisualSearchModal";
import { useNavbarBranding, NavbarBrandIdentity, NavbarBrandingConfig } from "./brandingConfig";

export interface NavbarV18CybercommandRoyalVioletProps {
  subdomain?: string;
  storeName?: string;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  contactAddress?: string;
  showAnnouncementBar?: boolean;
  announcementText?: string;
  announcementPhone?: string;
  storeTagline?: string;
  brandingConfig?: Partial<NavbarBrandingConfig>;
  isPreview?: boolean;
}

const BANGLADESH_LOCATIONS = [
  { name: "Khulna", code: "9000", label: "Khulna (9000) - Khulna Sadar" },
  { name: "Dhaka", code: "1207", label: "Dhaka (1207) - Mohammadpur / Dhanmondi" },
  { name: "Dhaka", code: "1212", label: "Dhaka (1212) - Gulshan / Banani" },
  { name: "Dhaka", code: "1216", label: "Dhaka (1216) - Mirpur" },
  { name: "Chittagong", code: "4000", label: "Chittagong (4000) - Agrabad / GEC" },
  { name: "Sylhet", code: "3100", label: "Sylhet (3100) - Zindabazar" },
  { name: "Rajshahi", code: "6000", label: "Rajshahi (6000) - Boalia" },
  { name: "Barisal", code: "8200", label: "Barisal (8200) - Sadar" },
  { name: "Rangpur", code: "5400", label: "Rangpur (5400) - Modern Mor" },
  { name: "Gazipur", code: "1700", label: "Gazipur (1700) - Joydebpur" },
  { name: "Comilla", code: "3500", label: "Comilla (3500) - Kandirpar" },
];

export default function NavbarV18CybercommandRoyalViolet({
  subdomain = "nextlook",
  storeName = "NextLook",
  logo = "/logo.png",
  primaryColor = "#059669",
  secondaryColor = "#047857",
  contactAddress = "Khulna 9000",
  showAnnouncementBar = true,
  announcementText = "Flash Deal: Use code XYLO20 for 20% OFF storewide!",
  announcementPhone = "+880 1700-000000",
  isPreview = false,
}: NavbarV18CybercommandRoyalVioletProps) {
  const router = useRouter();
  const cartContext = useCart?.() as any;

  // Variant Design Constants (Variant #18 - Cyber Command Deck - RoyalViolet)
  const variantNumber = 18;
  const archKey = "cyber_command";
  const archLabel = "Cyber Command Deck";
  const palName = "RoyalViolet";
  const grad = "from-violet-600 via-purple-600 to-indigo-700";
  const lightBg = "bg-violet-50";
  const textCol = "text-violet-700";
  const borderCol = "border-violet-200";
  const btnCls = "bg-violet-600 hover:bg-violet-700";
  const isDark = true;

  const {
    branding,
    effectiveStoreName,
    effectiveTagline,
    effectiveLogo,
    effectivePrimaryColor,
    effectiveAnnouncementText,
    effectiveAnnouncementPhone,
    effectiveShowAnnouncementBar,
    effectiveContactAddress,
    isCustomColor,
    topBarStyle,
    mainBarStyle,
    subBarStyle,
    accentBtnStyle,
  } = useNavbarBranding({
    subdomain,
    storeName,
    logo,
    primaryColor,
    secondaryColor,
    contactAddress,
    showAnnouncementBar,
    announcementText,
    announcementPhone,
  });

  storeName = effectiveStoreName || storeName;
  logo = effectiveLogo;
  primaryColor = effectivePrimaryColor;
  showAnnouncementBar = effectiveShowAnnouncementBar;
  announcementText = effectiveAnnouncementText;
  announcementPhone = effectiveAnnouncementPhone;
  contactAddress = effectiveContactAddress;

  const totalItems =
    typeof cartContext?.totalItems === "number"
      ? cartContext.totalItems
      : typeof cartContext?.total_items === "number"
      ? cartContext.total_items
      : Array.isArray(cartContext?.cart)
      ? cartContext.cart.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : Array.isArray(cartContext?.cartItems)
      ? cartContext.cartItems.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : 0;

  // Shop / Outlet States
  const [shopsList, setShopsList] = useState<ShopItem[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("all");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isAllShopsModalOpen, setIsAllShopsModalOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);

  // Live Search & Category Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string }>({
    id: "all",
    name: "All",
  });
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Mobile Search, Visual Search & Location Modal States
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(contactAddress);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [customZipInput, setCustomZipInput] = useState("");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const shopParam = params.get("shop");
      setSelectedShopId(shopParam || "all");
      const savedLoc = localStorage.getItem("store_delivery_location");
      if (savedLoc) setCurrentLocation(savedLoc);
    }
  }, []);

  // Fetch Shops / Outlets
  useEffect(() => {
    if (isPreview) {
      setShopsLoading(false);
      return;
    }
    async function fetchShops() {
      setShopsLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");
        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const res = await fetch(`${apiBase}/api/v1/stores/shops/`, {
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShopsList(list);
        }
      } catch (err) {
        console.error("Error fetching shops in NavbarV18CybercommandRoyalViolet:", err);
      } finally {
        setShopsLoading(false);
      }
    }
    fetchShops();
  }, [subdomain, isPreview]);

  // Fetch Categories
  useEffect(() => {
    if (isPreview) return;
    async function fetchCategories() {
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");
        const apiUrl = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/products/categories/`
          : `/api/v1/products/categories/`;
        const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.results || [];
          setCategoriesList(list);
        }
      } catch (err) {
        console.error("Error fetching categories in NavbarV18CybercommandRoyalViolet:", err);
      }
    }
    fetchCategories();
  }, [subdomain, isPreview]);

  // Live Auto-Suggestion Product Search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2 || isPreview) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");
        const params = new URLSearchParams({ search: searchQuery.trim() });
        if (selectedCategory.id !== "all") params.append("category", selectedCategory.id);
        if (selectedShopId !== "all") params.append("shop", selectedShopId);

        const apiUrl = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/products/?${params.toString()}`
          : `/api/v1/products/?${params.toString()}`;
        const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.results || [];
          setSuggestions(list.slice(0, 6));
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error("Live search error in NavbarV18CybercommandRoyalViolet:", err);
      } finally {
        setIsSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, selectedShopId, subdomain, isPreview]);

  // Outside click handler
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setIsCategoryMenuOpen(false);
      }
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target as Node)) {
        setIsShopMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectLocation = (locText: string) => {
    setCurrentLocation(locText);
    if (typeof window !== "undefined") {
      localStorage.setItem("store_delivery_location", locText);
    }
    setIsLocationModalOpen(false);
  };

  const handleSelectShop = (shopId: string | number) => {
    setIsShopMenuOpen(false);
    setIsAllShopsModalOpen(false);
    setSelectedShopId(String(shopId));
    if (!isPreview) {
      router.push(shopId === "all" ? "/products" : `/products?shop=${shopId}`);
    }
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setShowSuggestions(false);
    setIsMobileSearchOpen(false);
    if (searchQuery.trim() && !isPreview) {
      const catParam = selectedCategory.id !== "all" ? `&category=${selectedCategory.id}` : "";
      const shopParam = selectedShopId !== "all" ? `&shop=${selectedShopId}` : "";
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}${catParam}${shopParam}`);
    }
  };

  const resolveImgUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const currentSelectedShop = shopsList.find((s) => String(s.id) === selectedShopId);
  const currentShopLabel = currentSelectedShop ? currentSelectedShop.name : "All Outlets";

  return (
    <header className="w-full z-40 relative select-none font-sans">
      {/* 1. Top Promo & Announcement Strip */}
      {showAnnouncementBar && (
        <div style={topBarStyle} className={`w-full py-1.5 px-3 sm:px-6 text-[11px] font-bold ${isCustomColor ? "" : `bg-gradient-to-r ${grad}`} text-white`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 truncate">
              <span className="px-2 py-0.5 rounded-full bg-black/25 text-[9px] font-black uppercase tracking-wider font-mono">
                V{variantNumber} • {palName}
              </span>
              <span className="truncate">{announcementText}</span>
            </div>

            <div className="hidden sm:flex items-center gap-3 shrink-0 text-[10px]">
              <button
                type="button"
                onClick={() => setIsVisualSearchOpen(true)}
                className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-white font-black cursor-pointer transition-all"
                title="AI Visual Search (Upload Photo or Live Camera)"
              >
                <Camera className="w-3 h-3" />
                <span>AI Visual Search</span>
              </button>
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(true)}
                className="flex items-center gap-1 hover:underline cursor-pointer"
              >
                <MapPin className="w-3 h-3" /> {currentLocation}
              </button>
              <span className="flex items-center gap-1 font-mono">
                <Phone className="w-3 h-3" /> {announcementPhone}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 2. Main Archetype Header Container (Cyber Command Deck) */}
      <div className={archKey === "capsule_island" || archKey === "floating_dock" || archKey === "bento_header" || archKey === "clay_soft" ? "px-2 sm:px-6" : ""}>
        <div className={`bg-slate-950/95 backdrop-blur-xl text-white ${borderCol} w-full border-b-2 shadow-[0_10px_30px_rgba(0,0,0,0.45)] px-3 sm:px-6 py-3 relative transition-all`}>
          {/* 2.A. Mobile Full-Width Search Overlay */}
          {isMobileSearchOpen && (
            <div className="absolute inset-0 z-50 bg-slate-950/95 px-3 flex items-center gap-2 rounded-inherit animate-in fade-in duration-150">
              <button
                type="button"
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setShowSuggestions(false);
                }}
                className="p-1.5 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 cursor-pointer shrink-0"
                aria-label="Close Search"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <form
                onSubmit={handleSearchSubmit}
                className="flex-1 flex items-center h-10 bg-white rounded-xl overflow-hidden shadow-md"
              >
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search in ${storeName}...`}
                  className="flex-1 h-full px-3 text-xs text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSuggestions([]);
                    }}
                    className="p-1.5 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileSearchOpen(false);
                    setIsVisualSearchOpen(true);
                  }}
                  title="AI Visual Search by Photo or Camera"
                  className="p-2 text-indigo-600 hover:bg-indigo-50 cursor-pointer shrink-0"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <button
                  type="submit"
                  className={`h-full px-3.5 text-white text-xs font-black bg-gradient-to-r ${grad} cursor-pointer shrink-0`}
                >
                  <Search className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}

          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2.5 sm:gap-5">
            {/* Left: Mobile Drawer Trigger + Brand Identity */}
            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                className={`lg:hidden p-2 rounded-xl border cursor-pointer ${
                  isDark
                    ? "bg-white/5 border-white/10 text-white hover:bg-white/10"
                    : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                }`}
                aria-label="Open Menu"
              >
                <Menu className="w-4 h-4" />
              </button>

              <NavbarBrandIdentity
                href="/"
                branding={branding}
                storeName={storeName}
                tagline={effectiveTagline}
                logo={logo}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                isDarkTheme={isDark}
                variantStyle="archetype"
                archetypeBadgeText={archLabel.toUpperCase()}
                archetypeGradClass={grad}
                archetypeTextClass={isDark ? "text-emerald-400" : textCol}
              />

              {/* Outlet Selector Dropdown (Desktop) */}
              <div ref={shopDropdownRef} className="relative hidden xl:flex items-center ml-1">
                <button
                  type="button"
                  onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                    isDark
                      ? "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
                      : `${lightBg} ${borderCol} ${textCol}`
                  }`}
                  title="Select Outlet"
                >
                  <Store className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate max-w-[95px]">{currentShopLabel}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isShopMenuOpen ? "rotate-180" : ""}`} />
                </button>

                {isShopMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[999] py-1.5 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
                    <div className="px-3.5 py-1.5 border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Select Shop / Outlet
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSelectShop("all")}
                      className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                        selectedShopId === "all" ? "bg-slate-100 font-black text-emerald-600" : ""
                      }`}
                    >
                      <span>🏪 Main Store (All)</span>
                      {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                    </button>
                    {shopsLoading ? (
                      <div className="px-3.5 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Loading outlets...</span>
                      </div>
                    ) : (
                      shopsList.map((shop) => (
                        <button
                          key={shop.id}
                          type="button"
                          onClick={() => handleSelectShop(shop.id)}
                          className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                            String(shop.id) === selectedShopId ? "bg-slate-100 font-bold text-emerald-600" : "text-slate-700"
                          }`}
                        >
                          <span className="truncate">{shop.name}</span>
                          {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Explore Brand Shops Pill */}
              <button
                type="button"
                onClick={() => setIsAllShopsModalOpen(true)}
                className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-black cursor-pointer transition-all ${lightBg} ${borderCol} ${textCol}`}
                title="Explore All Brand Shops"
              >
                <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                <span>Shops</span>
              </button>
            </div>

            {/* Center: Live Search Bar with Category Filter & Integrated AI Visual Search Camera Button */}
            <div ref={searchBoxRef} className="order-3 lg:order-2 w-full lg:flex-1 lg:max-w-xl relative">
              <form
                onSubmit={handleSearchSubmit}
                className={`flex items-center rounded-2xl overflow-visible border-2 transition-all bg-slate-900/90 border-slate-700/80 text-white focus-within:border-emerald-400 ${borderCol}`}
              >
                {/* Category Selector Dropdown */}
                <div ref={categoryDropdownRef} className="relative hidden sm:block h-full shrink-0">
                  <button
                    type="button"
                    onClick={() => setIsCategoryMenuOpen(!isCategoryMenuOpen)}
                    className={`h-full px-3 py-2 text-[11px] font-bold border-r flex items-center gap-1 cursor-pointer ${
                      isDark ? "border-white/10 text-slate-300" : "border-slate-200 text-slate-600"
                    }`}
                  >
                    <span className="truncate max-w-[75px]">{selectedCategory.name}</span>
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>

                  {isCategoryMenuOpen && (
                    <div className="absolute top-full left-0 mt-1.5 w-56 max-h-64 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-y-auto z-[999] py-1 text-slate-800">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory({ id: "all", name: "All" });
                          setIsCategoryMenuOpen(false);
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer"
                      >
                        <span>All Categories</span>
                        {selectedCategory.id === "all" && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                      </button>
                      {categoriesList.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setSelectedCategory({ id: cat.slug || String(cat.id), name: cat.name });
                            setIsCategoryMenuOpen(false);
                          }}
                          className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate"
                        >
                          <span className="truncate">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pl-3 pr-1.5 text-slate-400">
                  <Search className="w-4 h-4" />
                </div>

                <input
                  type="text"
                  value={searchQuery}
                  onFocus={() => {
                    if (suggestions.length > 0) setShowSuggestions(true);
                  }}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search in ${storeName} or click camera for AI Visual Search...`}
                  className="w-full py-2 px-1 text-xs font-semibold bg-transparent focus:outline-none"
                />

                {isSearching ? (
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin mr-1 shrink-0" />
                ) : searchQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSuggestions([]);
                    }}
                    className="p-1 mr-1 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : null}

                {/* 🌟 AI Visual Search Camera / Lens Button inside Search Input */}
                <button
                  type="button"
                  onClick={() => {
                    setShowSuggestions(false);
                    setIsVisualSearchOpen(true);
                  }}
                  className={`p-1.5 mx-1 rounded-xl transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0 ${
                    isDark
                      ? "bg-white/10 hover:bg-white/20 text-white"
                      : `${lightBg} ${textCol} border ${borderCol} hover:opacity-90`
                  }`}
                  title="AI Visual Search (Upload Photo or Use Live Camera)"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline text-[10px] font-black">Lens</span>
                </button>

                <button
                  type="submit"
                  style={accentBtnStyle}
                  className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white ${isCustomColor ? "" : `bg-gradient-to-r ${grad}`} rounded-r-xl cursor-pointer shrink-0`}
                >
                  Search
                </button>
              </form>

              {/* Auto-Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-[998] text-slate-900">
                  <div className="p-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase px-3">
                    <span>Matching Products</span>
                    <span>{suggestions.length} results</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {suggestions.map((item) => {
                      const img = resolveImgUrl(item.primary_image || item.image);
                      return (
                        <Link
                          key={item.id}
                          href={`/products/${item.slug || item.id}`}
                          onClick={() => setShowSuggestions(false)}
                          className="flex items-center gap-3 p-2.5 hover:bg-slate-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0 border border-slate-200 flex items-center justify-center">
                            {img ? (
                              <img src={img} alt={item.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-xs font-bold text-slate-800 truncate">{item.name}</h5>
                            <p className="text-[10px] text-slate-400 truncate">{item.category_name || "Store Item"}</p>
                          </div>
                          <div className={`text-xs font-black shrink-0 ${textCol}`}>
                            ৳{Math.round(Number(item.price || 0)).toLocaleString()}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Actions: Visual Search + User Menu + Cart */}
            <div className="order-2 lg:order-3 flex items-center gap-1.5 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsVisualSearchOpen(true)}
                className={`hidden md:flex p-2 sm:px-3 sm:py-1.5 rounded-xl border text-xs font-black items-center gap-1.5 cursor-pointer transition-all active:scale-95 ${
                  isDark
                    ? "bg-white/10 border-white/15 text-white hover:bg-white/20"
                    : `${lightBg} ${borderCol} ${textCol}`
                }`}
                title="AI Visual Search"
              >
                <Camera className="w-4 h-4 shrink-0" />
                <span className="hidden xl:inline">Visual Search</span>
              </button>

              <button
                type="button"
                onClick={() => setIsMobileSearchOpen(true)}
                className={`lg:hidden p-2 rounded-xl border cursor-pointer ${
                  isDark
                    ? "bg-white/5 border-white/10 text-white"
                    : "bg-slate-100 border-slate-200 text-slate-700"
                }`}
                aria-label="Open Search"
              >
                <Search className="w-4 h-4" />
              </button>

              {isPreview ? (
                <div className="flex items-center gap-2">
                  <div
                    className={`p-2 rounded-xl border ${
                      isDark ? "bg-white/5 border-white/10 text-white" : "bg-slate-100 border-slate-200 text-slate-700"
                    }`}
                  >
                    <Heart className="w-4 h-4" />
                  </div>
                  <div className={`px-3 py-2 rounded-xl text-white text-xs font-black flex items-center gap-1.5 shadow-sm ${btnCls}`}>
                    <ShoppingCart className="w-3.5 h-3.5" />
                    <span>Cart ({totalItems || 3})</span>
                  </div>
                </div>
              ) : (
                <>
                  <Link
                    href="/wishlist"
                    className={`hidden sm:flex p-2 rounded-xl border transition-colors ${
                      isDark
                        ? "bg-white/5 border-white/10 text-white hover:bg-white/10"
                        : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                    }`}
                    title="Wishlist"
                  >
                    <Heart className="w-4 h-4" />
                  </Link>
                  <UserHeaderMenu />
                  <CartHeaderButton />
                </>
              )}
            </div>
          </div>

          {/* 3. Sub-Navigation & Quick Links Strip */}
          <div className={`bg-slate-900/95 border-t border-slate-800 text-slate-300 mt-2.5 pt-2 px-2 flex items-center justify-between text-[11px] font-bold overflow-x-auto whitespace-nowrap [scrollbar-width:none]`}>
            <div className="flex items-center gap-4 shrink-0">
              <Link href="/" className="hover:opacity-80 transition-opacity">
                Home
              </Link>
              <Link href="/products" className="hover:opacity-80 transition-opacity">
                All Products
              </Link>
              <Link href="/categories" className="hover:opacity-80 transition-opacity flex items-center gap-1">
                <Layers className="w-3 h-3" /> Categories
              </Link>
              <Link href="/products?deals=true" className="hover:opacity-80 transition-opacity flex items-center gap-1">
                <Flame className="w-3 h-3 text-amber-500" /> Hot Deals
              </Link>
              <button
                type="button"
                onClick={() => setIsVisualSearchOpen(true)}
                className="flex items-center gap-1 hover:underline font-black cursor-pointer"
              >
                <Camera className="w-3 h-3" /> Search by Photo / Camera
              </button>
            </div>

            <span className="hidden md:flex items-center gap-1 text-[10px] opacity-80 shrink-0 ml-3">
              <ShieldCheck className="w-3.5 h-3.5" /> {archLabel} • {palName}
            </span>
          </div>
        </div>
      </div>

      {/* 4. Brand Shops Story Modal */}
      <ShopCardsModal
        isOpen={isAllShopsModalOpen}
        onClose={() => setIsAllShopsModalOpen(false)}
        shopsList={shopsList}
        shopsLoading={shopsLoading}
        onSelectShop={handleSelectShop}
        primaryColor={primaryColor}
      />

      {/* 5. AI Visual Search Modal (Upload Photo & Live Camera) */}
      <VisualSearchModal
        isOpen={isVisualSearchOpen}
        onClose={() => setIsVisualSearchOpen(false)}
        subdomain={subdomain}
        primaryColor={primaryColor}
      />

      {/* 6. Delivery Location Picker Modal */}
      {isLocationModalOpen && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 z-10 text-slate-900 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-600" />
                <h3 className="text-sm sm:text-base font-black text-slate-900">Choose delivery location</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {BANGLADESH_LOCATIONS.map((loc, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectLocation(`${loc.name} ${loc.code}`)}
                  className="w-full p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-left text-xs font-bold text-slate-700 flex items-center justify-between cursor-pointer"
                >
                  <span className="truncate">{loc.label}</span>
                  {currentLocation.includes(loc.code) && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
                </button>
              ))}
            </div>

            <div className="pt-2 border-t border-slate-100 flex gap-2">
              <input
                type="text"
                value={customZipInput}
                onChange={(e) => setCustomZipInput(e.target.value)}
                placeholder="Enter Area or Postcode..."
                className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  if (customZipInput.trim()) {
                    handleSelectLocation(customZipInput.trim());
                    setCustomZipInput("");
                  }
                }}
                className={`px-4 py-2 text-white text-xs font-bold rounded-xl cursor-pointer ${btnCls}`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. Responsive Mobile Navigation Drawer */}
      {mobileDrawerOpen && (
        <div
          className="fixed inset-0 z-[999998] bg-slate-950/60 backdrop-blur-xs lg:hidden flex"
          onClick={() => setMobileDrawerOpen(false)}
        >
          <div
            className="w-72 max-w-[85vw] bg-white h-full shadow-2xl p-5 flex flex-col justify-between text-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4 overflow-y-auto">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="font-black text-base capitalize">{storeName}</span>
                <button
                  type="button"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-1.5 rounded-xl bg-slate-100 text-slate-600 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-1.5 text-xs font-bold">
                <Link
                  href="/"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="block px-3 py-2.5 rounded-xl hover:bg-slate-100"
                >
                  Home
                </Link>
                <Link
                  href="/products"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="block px-3 py-2.5 rounded-xl hover:bg-slate-100"
                >
                  All Products
                </Link>
                <Link
                  href="/categories"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="block px-3 py-2.5 rounded-xl hover:bg-slate-100"
                >
                  Categories
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    setIsAllShopsModalOpen(true);
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-slate-100 font-black text-purple-700 flex items-center gap-2 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span>Explore All Brand Shops</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    setIsVisualSearchOpen(true);
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl font-black flex items-center gap-2 cursor-pointer ${lightBg} ${textCol}`}
                >
                  <Camera className="w-4 h-4" />
                  <span>AI Visual Search (Camera/Photo)</span>
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between">
              <span className="font-bold text-slate-600">{storeName}</span>
              <span>V{variantNumber} • {palName}</span>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
